import {
  type ArchiveBlock,
  EditionArchive,
} from '@/components/whats-new/edition-archive';
import { FeaturedEditionCard } from '@/components/whats-new/featured-edition-card';
import { useWhatsNew } from '@/components/whats-new/whats-new-provider';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTenant } from '@/hooks/use-tenant';
import { useI18nContext } from '@/i18n/i18n-react';
import { SidebarLayout } from '@/layouts/sidebar.layout';
import type {
  CatalogEdition,
  EditionModule,
} from '@/lib/whats-new/edition-types';
import { EDITIONS } from '@/lib/whats-new/registry';

/**
 * Editions the page shows, newest first: the catalog's that have a module
 * or, while the catalog is loading, failed or is empty, every module the
 * admin has (`inCatalog: false`: whether it was seen is unknown).
 */
function shownEditions(catalog: readonly CatalogEdition[]): {
  modules: EditionModule[];
  inCatalog: boolean;
} {
  const modules = catalog.flatMap((edition) =>
    Object.prototype.hasOwnProperty.call(EDITIONS, edition.id)
      ? [EDITIONS[edition.id]]
      : []
  );
  return modules.length > 0
    ? { modules, inCatalog: true }
    : { modules: Object.values(EDITIONS), inCatalog: false };
}

/**
 * "Novidades" (`/:tenantSlug/whats-new`, NEWS-37…43): the header (no torus),
 * the newest edition's navy card and the archive of every edition.
 */
export default function WhatsNewPage() {
  const { LL } = useI18nContext();
  const tenant = useTenant();
  const isMobile = useIsMobile();
  const { catalog, isSeen, open } = useWhatsNew();
  const { modules, inCatalog } = shownEditions(catalog);
  const featured = modules[0];
  const blocks: ArchiveBlock[] = modules.flatMap((edition) =>
    edition.archive.map((block) => ({ editionId: edition.id, block }))
  );

  return (
    <SidebarLayout>
      <div className="mx-auto flex w-full min-w-0 max-w-[1180px] flex-col">
        <header
          data-testid="whats-new-header"
          className="flex flex-col gap-[18px] pt-2"
        >
          <div className="flex items-center gap-3 whitespace-nowrap text-[11px] font-semibold uppercase tracking-[0.16em] text-[#155DFC] md:gap-3.5 md:text-xs">
            <span>{LL.whatsNew.page.eyebrowLead()}</span>
            <span aria-hidden="true" className="h-px w-12 bg-[#155DFC]/35" />
            <span>{LL.whatsNew.page.eyebrowTrail()}</span>
          </div>
          <h1 className="text-balance text-[64px] font-medium leading-[1.02] tracking-[-0.035em] text-[#15191E] max-md:text-[38px]">
            {LL.whatsNew.page.title()}
          </h1>
          <p className="max-w-[520px] text-[15px] leading-relaxed text-[#4B5361]">
            {LL.whatsNew.page.lead({ store: tenant.name })}
          </p>
        </header>

        {featured && (
          <div className="mb-10 mt-6">
            <FeaturedEditionCard
              edition={featured}
              seen={inCatalog ? isSeen(featured.id) : null}
              showTorus={!isMobile}
              onWatch={() => open(featured.id)}
            />
          </div>
        )}

        <EditionArchive blocks={blocks} onSee={open} />
      </div>
    </SidebarLayout>
  );
}
